import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  InputField,
  MultiSelectChips,
  Select,
  SubmitButton,
} from '@saas-kit/ui';

import type { QmsCounterGroup, QmsService } from './types';

export interface CounterCreateInput {
  code: string;
  name: string;
  group_id: string | null;
  service_ids: string[];
}

export function CounterCreateForm({
  groups,
  services,
  creating,
  onCreate,
}: {
  groups: QmsCounterGroup[];
  services: QmsService[];
  creating: boolean;
  onCreate: (input: CounterCreateInput) => Promise<unknown>;
}): JSX.Element {
  const { t } = useTranslation('qms');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [groupId, setGroupId] = useState('');
  const [serviceIds, setServiceIds] = useState<string[]>([]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !name.trim()) return;
    await onCreate({
      code: code.trim(),
      name: name.trim(),
      group_id: groupId || null,
      service_ids: serviceIds,
    });
    setCode('');
    setName('');
    setGroupId('');
    setServiceIds([]);
  };

  return (
    <Card>
      <CardContent className="p-5">
        <form onSubmit={(e) => void submit(e)} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <InputField
              label={t('fields.code')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t('placeholders.counter_code')}
              required
            />
            <InputField
              label={t('fields.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('placeholders.counter_name')}
              required
            />
            <label className="block space-y-1 text-sm">
              <span className="text-foreground">{t('fields.group')}</span>
              <Select
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
              >
                <option value="">{t('fields.none')}</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </Select>
            </label>
          </div>
          <MultiSelectChips
            label={t('fields.services')}
            options={services.map((s) => ({ value: s.id, label: s.name }))}
            value={serviceIds}
            onChange={setServiceIds}
            selectAllLabel={t('actions.select_all')}
            clearLabel={t('actions.clear_all')}
          />
          <SubmitButton
            loading={creating}
            label={t('actions.create')}
            loadingLabel={t('actions.create')}
          />
        </form>
      </CardContent>
    </Card>
  );
}
